import React, { createContext, useContext, useEffect, useState } from 'react';
import { getMe, login as apiLogin,logout as logoutApi} from '../api/authApi';

interface AuthContextType {
  user: any;
  loading: boolean;
  login: (credentials: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  /** True when the user holds at least one of the given permissions. */
  hasAnyPermission: (...permissions: string[]) => boolean;
  /** True when the user holds every one of the given permissions. */
  hasAllPermissions: (...permissions: string[]) => boolean;
  /** Every permission name the signed-in user holds. */
  permissions: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true); // ✅ new state

  const login = async ({ username, password }: { username: string; password: string }) => {
    await apiLogin(username, password);
    const me = await getMe();
    setUser(me);
  };
  // `/me` returns the user's role with its permissions expanded. Older
  // responses put them on `user.permissions`, so both shapes are accepted.
  const permissions: string[] = React.useMemo(() => {
    if (!user) return [];
    const fromRole = user.role?.permissions ?? [];
    const fromRoles = (user.roles ?? []).flatMap((role: any) => role?.permissions ?? []);
    const direct = user.permissions ?? [];
    return Array.from(
      new Set(
        [...fromRole, ...fromRoles, ...direct]
          .map((permission: any) => (typeof permission === 'string' ? permission : permission?.name))
          .filter(Boolean)
      )
    );
  }, [user]);

  const hasPermission = (permission: string): boolean => permissions.includes(permission);

  const hasAnyPermission = (...names: string[]): boolean =>
    names.some(name => permissions.includes(name));

  const hasAllPermissions = (...names: string[]): boolean =>
    names.every(name => permissions.includes(name));
  const logout = () => {
    setUser(null);
    logoutApi()
      .then(() => {
        setUser(null);
      })
      .catch((error) => {
        // Error handled by API layer
      });
  };

  useEffect(() => {
    getMe()
      .then((me) => setUser(me))
      .catch(() => setUser(null))
      .finally(() => setLoading(false)); // ✅ stop loading even if failed
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, hasPermission, hasAnyPermission, hasAllPermissions, permissions }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be inside AuthProvider");
  return context;
};
